import { useEffect, useState } from 'react';

export interface TmtSpickMetrics {
  height: number;
  guidanceHeight: number;
  screenPb: number;
}

// 게임판 하단 최소 여백 (12~16px 확보)
const MIN_BOTTOM_GAP = 16;

// 실제 사용 가능한 viewport 높이를 읽어 스픽 공통 레이아웃 지표를 계산한다.
// 브라우저 주소창/하단 메뉴 등 UI 변화는 visualViewport.height 로 감지하고,
// 100dvh 를 fallback 으로 사용해 주소창이 접히고 펼쳐져도 레이아웃이 깨지지 않게 한다.
function computeMetrics(): TmtSpickMetrics {
  const visualViewport =
    typeof window !== 'undefined' ? window.visualViewport : undefined;
  const height = Math.round(
    visualViewport?.height ??
      (typeof window !== 'undefined' ? window.innerHeight : 800),
  );

  let guidanceHeight: number;
  if (height >= 780) {
    guidanceHeight = 76;
  } else if (height >= 660) {
    guidanceHeight = 68;
  } else if (height >= 560) {
    guidanceHeight = 58;
  } else {
    guidanceHeight = 52;
  }

  return {
    height,
    guidanceHeight,
    screenPb: Math.max(MIN_BOTTOM_GAP, height >= 780 ? 20 : MIN_BOTTOM_GAP),
  };
}

function applyMetrics(m: TmtSpickMetrics): void {
  const root = document.documentElement.style;
  root.setProperty('--sh-app-height', `${m.height}px`);
  root.setProperty('--sh-guidance-height', `${m.guidanceHeight}px`);
  root.setProperty('--sh-screen-pb', `${m.screenPb}px`);
}

export function useViewportMetrics(): TmtSpickMetrics {
  const [metrics, setMetrics] = useState<TmtSpickMetrics>(() => computeMetrics());

  useEffect(() => {
    const visualViewport = window.visualViewport;
    const update = () => {
      const next = computeMetrics();
      setMetrics(next);
      applyMetrics(next);
    };

    update();

    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);

    return () => {
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return metrics;
}